(() => {
  if (window.__hemiProductCardSpeedReady) return;
  window.__hemiProductCardSpeedReady = true;

  const CARD_SELECTOR = 'product-item, .product-collection, .alp-pc5, .alp-product-item, [data-product-id]';
  const SWATCH_SELECTOR = '[data-js-option-value], .alp-pc5__swatch, .product-options__value, [data-value]';
  const QUICK_ADD_SELECTOR = '[data-product-variants]';
  const imageCache = new Set();

  const normalize = (value) => String(value || '').trim().toLowerCase();

  const parseJsonAttr = (node, attr) => {
    if (!node) return null;
    const raw = node.getAttribute(attr);
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch (error) {
      return null;
    }
  };

  const imageUrl = (url, width = 720) => {
    if (!url) return '';
    const absolute = url.startsWith('//') ? `${window.location.protocol}${url}` : url;
    try {
      const parsed = new URL(absolute, window.location.origin);
      parsed.searchParams.set('width', String(width));
      return parsed.toString();
    } catch (error) {
      return absolute;
    }
  };

  const srcsetFor = (url) => {
    const widths = [360, 540, 720, 900];
    return widths.map((width) => `${imageUrl(url, width)} ${width}w`).join(', ');
  };

  const preload = (url) => {
    if (!url) return;
    const cacheKey = imageUrl(url, 720);
    if (imageCache.has(cacheKey)) return;
    imageCache.add(cacheKey);
    const image = new Image();
    image.decoding = 'async';
    image.src = cacheKey;
    image.srcset = srcsetFor(url);
    image.sizes = '(max-width: 767px) 50vw, 25vw';
  };

  const variantImageUrl = (variant) => {
    if (!variant) return '';
    if (variant.featured_image && variant.featured_image.src) return variant.featured_image.src;
    if (variant.featured_media && variant.featured_media.preview_image && variant.featured_media.preview_image.src) return variant.featured_media.preview_image.src;
    return '';
  };

  const cardFor = (target) => target.closest(CARD_SELECTOR);

  const getVariants = (card) => {
    const source = card && card.querySelector(QUICK_ADD_SELECTOR);
    const variants = parseJsonAttr(source, 'data-product-variants');
    return Array.isArray(variants) ? variants : [];
  };

  const swatchValue = (swatch) => normalize(
    swatch && (
      swatch.getAttribute('data-value') ||
      swatch.getAttribute('aria-label') ||
      swatch.getAttribute('title') ||
      swatch.textContent
    )
  );

  const variantMatchesValue = (variant, value) => {
    if (!variant || !value) return false;
    const options = [variant.option1, variant.option2, variant.option3]
      .concat(Array.isArray(variant.options) ? variant.options : [])
      .filter(Boolean)
      .map(normalize);
    return options.includes(value);
  };

  const variantForSwatch = (card, swatch) => {
    const value = swatchValue(swatch);
    return getVariants(card).find((variant) => variantMatchesValue(variant, value));
  };

  const imageNodes = (card) => {
    if (!card) return [];
    return Array.from(card.querySelectorAll('.alp-pc5__image-wrap img, .product-collection__image img, [data-js-product-image] img'));
  };

  const updatePictureSources = (img, url) => {
    const picture = img.closest('picture');
    if (!picture) return;
    picture.querySelectorAll('source').forEach((source) => {
      source.srcset = srcsetFor(url);
      source.dataset.srcset = source.srcset;
    });
  };

  const switchImage = (card, url) => {
    if (!url) return;
    imageNodes(card).forEach((img) => {
      const nextSrc = imageUrl(url, 720);
      if (img.src === nextSrc) return;
      img.decoding = 'async';
      img.loading = 'eager';
      img.sizes = img.sizes || '(max-width: 767px) 50vw, 25vw';
      img.srcset = srcsetFor(url);
      img.dataset.srcset = img.srcset;
      img.src = nextSrc;
      img.dataset.src = nextSrc;
      updatePictureSources(img, url);
    });
  };

  const preloadCard = (card) => {
    if (!card || card.dataset.hemiImagesPreloaded === 'true') return;
    card.dataset.hemiImagesPreloaded = 'true';
    getVariants(card).forEach((variant) => preload(variantImageUrl(variant)));
  };

  const observeCards = () => {
    const cards = Array.from(document.querySelectorAll(CARD_SELECTOR)).filter((card) => card.querySelector(QUICK_ADD_SELECTOR));
    if (!('IntersectionObserver' in window)) {
      cards.forEach(preloadCard);
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        preloadCard(entry.target);
        observer.unobserve(entry.target);
      });
    }, { rootMargin: '600px 0px' });
    cards.forEach((card) => observer.observe(card));
  };

  document.addEventListener('pointerenter', (event) => {
    const swatch = event.target.closest(SWATCH_SELECTOR);
    if (!swatch) return;
    const card = cardFor(swatch);
    const variant = variantForSwatch(card, swatch);
    preload(variantImageUrl(variant));
  }, true);

  document.addEventListener('touchstart', (event) => {
    const swatch = event.target.closest(SWATCH_SELECTOR);
    if (!swatch) return;
    const card = cardFor(swatch);
    const variant = variantForSwatch(card, swatch);
    preload(variantImageUrl(variant));
  }, { passive: true, capture: true });

  document.addEventListener('click', (event) => {
    const swatch = event.target.closest(SWATCH_SELECTOR);
    if (!swatch) return;
    const card = cardFor(swatch);
    const variant = variantForSwatch(card, swatch);
    const url = variantImageUrl(variant);
    if (!url) return;
    preload(url);
    requestAnimationFrame(() => switchImage(card, url));
  }, true);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', observeCards, { once: true });
  } else {
    observeCards();
  }

  document.addEventListener('shopify:section:load', observeCards);
})();
